import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildNewTabPath, buildCampaignsPath } from '../../utils/routing';
import { stackFromTab } from '../../components/DocsArea/useDocTabMemory';
import {
  applyTabPreferredView,
  startCampaignTabNavigation,
  startTemplateTabNavigation,
  navigateViewTab,
} from './tabClickNavigation';

/**
 * Tab-bar click navigation: blank / campaign / template / view / doc / docs / dashboards.
 * Returns false when a view-tab navigation was superseded (caller should skip URL sync).
 */
export function useTabClick({
  sprints,
  selectedSprintId,
  selectedTemplateId,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  setLastSelectedCampaignId,
  loadFullSprint,
  loadFullTemplate,
  handleSelectView,
  setIsLoadingItem,
  setPreferredDetailView,
  setPendingMissionFocus,
  clearActiveView,
  activeView,
  currentView,
  VIEWS,
}) {
  const navigate = useNavigate();
  // Bumps on each tab navigation; deferred work ignores stale runs when the user switches tabs quickly.
  const tabNavigationSeqRef = useRef(0);

  const handleTabClick = useCallback(async (tab) => {
    const seq = ++tabNavigationSeqRef.current;

    if (tab.type === 'blank') {
      clearActiveView?.();
      setSelectedSprintId(null);
      setSelectedTemplateId(null);
      setCurrentView(VIEWS.EMPTY);
      setIsLoadingItem?.(false);
      // Keep URL in sync when landing on a blank tab (e.g. after closing another tab)
      navigate(buildNewTabPath());
      return true;
    }

    if (tab.type === 'campaign') {
      const sprint = sprints.find((s) => s.id === tab.targetId);
      if (sprint) {
        startCampaignTabNavigation({
          sprint,
          isNew: sprint.id !== selectedSprintId,
          seq,
          tabNavigationSeqRef,
          clearActiveView,
          setIsLoadingItem,
          setSelectedSprintId,
          setSelectedTemplateId,
          setCurrentView,
          setLastSelectedCampaignId,
          loadFullSprint,
          VIEWS,
          currentView,
          tab,
          setPreferredDetailView,
          setPendingMissionFocus,
        });
      }
      return true;
    }

    if (tab.type === 'template') {
      const template = sprints.find((s) => s.id === tab.targetId);
      if (template) {
        startTemplateTabNavigation({
          template,
          isNew: template.id !== selectedTemplateId,
          seq,
          tabNavigationSeqRef,
          clearActiveView,
          setIsLoadingItem,
          setSelectedSprintId,
          setSelectedTemplateId,
          setCurrentView,
          loadFullTemplate,
          VIEWS,
          currentView,
          tab,
          setPreferredDetailView,
        });
      }
      return true;
    }

    applyTabPreferredView(tab, setPreferredDetailView);

    if (tab.type === 'view') {
      return navigateViewTab({
        tab,
        seq,
        tabNavigationSeqRef,
        activeViewId: activeView?.id,
        handleSelectView,
        setIsLoadingItem,
        setCurrentView,
        VIEWS,
        currentView,
      });
    }

    if (tab.type === 'docs') {
      setCurrentView(VIEWS.DOCS);
      setIsLoadingItem?.(false);
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('fokus:docs-home'));
      });
      return true;
    }

    if (tab.type === 'dashboards') {
      setCurrentView(VIEWS.DASHBOARDS);
      setIsLoadingItem?.(false);
      return true;
    }

    if (tab.type === 'campaigns') {
      clearActiveView?.();
      setSelectedSprintId(null);
      setSelectedTemplateId(null);
      setCurrentView(VIEWS.EMPTY);
      setIsLoadingItem?.(false);
      navigate(buildCampaignsPath());
      return true;
    }

    if (tab.type === 'doc') {
      setCurrentView(VIEWS.DOCS);
      setIsLoadingItem?.(false);
      if (tab.targetId) {
        const restored = stackFromTab(tab);
        requestAnimationFrame(() => {
          window.dispatchEvent(
            new CustomEvent('fokus:open-doc', {
              detail: { docId: tab.targetId, restore: true, docStack: restored },
            })
          );
        });
      }
      return true;
    }

    return true;
  }, [
    sprints,
    selectedSprintId,
    selectedTemplateId,
    activeView?.id,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    setLastSelectedCampaignId,
    loadFullSprint,
    loadFullTemplate,
    handleSelectView,
    setIsLoadingItem,
    setPreferredDetailView,
    setPendingMissionFocus,
    clearActiveView,
    currentView,
    VIEWS,
    navigate,
  ]);

  return { handleTabClick };
}
